import React, { useState } from 'react';
import { Save, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface UserProfileHeaderProps {
  user: any;
  profile: any;
  memberSince: string;
}

const UserProfileHeader: React.FC<UserProfileHeaderProps> = ({ user, profile, memberSince }) => {
  const { user: authUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [loading, setLoading] = useState(false);

  const getUserDisplayName = () => {
    // Use the name from profile first
    if (profile?.name) {
      return profile.name;
    }
    
    // Fallback to metadata if available
    if (user?.user_metadata?.first_name && user?.user_metadata?.last_name) {
      return `${user.user_metadata.first_name} ${user.user_metadata.last_name}`;
    }
    if (user?.user_metadata?.name) {
      return user.user_metadata.name;
    }
    if (user?.user_metadata?.full_name) {
      return user.user_metadata.full_name;
    }
    
    // Last resort: email prefix (but this should be rare now)
    return user?.email?.split('@')[0] || "User";
  };

  const handleEditClick = () => {
    setEditName(getUserDisplayName());
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!authUser || !editName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setLoading(true);
    try {
      // Update the profiles table
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          name: editName.trim()
        })
        .eq('id', authUser.id);

      if (profileError) {
        console.error('Error updating profile:', profileError);
        toast.error('Failed to save your name');
        return;
      }

      // Also update the user metadata to keep them in sync
      const { error: userError } = await supabase.auth.updateUser({
        data: {
          name: editName.trim(),
          display_name: editName.trim()
        }
      });

      if (userError) {
        console.error('Error updating user metadata:', userError);
        // Don't show error to user since profile was saved successfully
        console.log('Profile saved but user metadata update failed');
      }

      toast.success('Name updated successfully!');
      setIsEditing(false);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditName('');
  };

  return (
    <div className="pb-6 border-b dark:border-gray-800">
      <div className="flex items-center gap-2 mb-1">
        {isEditing ? (
          <div className="flex items-center gap-2 flex-1">
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="text-xl font-semibold"
              placeholder="Enter your name"
              autoFocus
            />
            <Button
              onClick={handleSave}
              size="sm"
              disabled={loading || !editName.trim()}
              className="h-8"
            >
              <Save className="w-4 h-4" />
            </Button>
            <Button
              onClick={handleCancel}
              variant="outline"
              size="sm"
              className="h-8"
            >
              Cancel
            </Button>
          </div>
        ) : (
          <>
            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
              {getUserDisplayName()}
            </h3>
            <Button
              onClick={handleEditClick}
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Pencil className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        {user?.email || "No email available"}
      </p>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
        Member since {memberSince}
      </p>
    </div>
  );
};

export default UserProfileHeader;
